/**
 * MatchModal - Create/Edit match wizard (3 steps)
 *
 * Steps:
 * 1. Match Details - Basic info, teams, assignments
 * 2. FTP Destinations - Select FTP targets for photo delivery
 * 3. IPTC Configuration - Metadata templates for exported photos
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import Modal from '../../../../shared/components/ui/Modal';
import Button from '../../../../shared/components/ui/Button';
import Stepper from '../../../../shared/components/ui/Stepper/Stepper';
import { EVENT_TYPES, COMPETITIONS, SPORTS, seasonOptions } from '../../utils/constants';
import { lookupVenueLocation } from '../../utils/venueLocations';
import { toLocalDateTimeInput } from '../../utils/dateUtils';
import FtpSelectionStep from './FtpSelectionStep';
import IptcConfigStep from './IptcConfigStep';
import styles from './MatchModal.module.css';
import {
  searchTeams,
  searchMatchHubFixtures,
  fetchTeamByExternalId,
  linkTeamToMatchHub,
} from '../../../../api';
import MatchHubSearchInput from '../../../../shared/components/MatchHubSearchInput';

/**
 * Free-text MatchHub fixture search (round 2). Rows surface league / status /
 * HOME-AWAY / date / score so a planner can pick at a glance. The editor types
 * `Feyenoord - Ajax`, `Feyenoord v Ajax`, `Feyenoord vs Ajax`, or just
 * `Feyenoord` — gateway parses with multi-separator support.
 */
function MatchHubSearch({ onSelect, onManualEntry }) {
  const renderRow = (fixture) => {
    const home = fixture.home?.name || '';
    const away = fixture.away?.name || '';
    const date = fixture.kickoff ? new Date(fixture.kickoff).toLocaleDateString() : '';
    const hasScore =
      fixture.goals?.home !== null &&
      fixture.goals?.home !== undefined &&
      fixture.goals?.away !== null &&
      fixture.goals?.away !== undefined;
    const score = hasScore ? `(${fixture.goals.home}-${fixture.goals.away})` : '';
    const status = fixture.status_long || fixture.status || '';
    const league = fixture.season?.competition?.name || '';
    return (
      <div className={styles.searchResult}>
        <span className={styles.resultTeams}>
          {home} vs {away}
        </span>
        <span className={styles.resultMeta}>
          {[league, date, score, status].filter(Boolean).join(' · ')}
        </span>
      </div>
    );
  };

  return (
    <div className={styles.matchHubSearch}>
      <div className={styles.searchHeader}>
        <label className={styles.label}>Link from MatchHub</label>
        <span className={styles.hint}>
          Type a fixture, e.g. <em>Feyenoord - Ajax</em>, <em>Feyenoord v Ajax</em>, or just{' '}
          <em>Feyenoord</em>.
        </span>
      </div>
      <MatchHubSearchInput
        searchFn={searchMatchHubFixtures}
        dataKey="matches"
        renderRow={renderRow}
        onSelect={(fx) => onSelect(fx)}
        placeholder="Feyenoord - Ajax…"
        inputClassName={styles.searchInput}
        listClassName={styles.searchResults}
        rowClassName={styles.searchResultRow}
      />

      <div className={styles.divider}>
        <span>or</span>
      </div>

      <Button variant="ghost" fullWidth onClick={onManualEntry}>
        Enter match details manually
      </Button>
    </div>
  );
}

/**
 * Inline create-with-override card shown for a fixture team that doesn't exist
 * locally. Editor edits the suggested name (`Excelsior` → `Excelsior Rotterdam`),
 * clicks Create + Link, and the new local Team is wired to the wizard via
 * `onCreated(team)`.
 */
function PendingTeamCard({ side, externalId, suggestedName, onCreated, onSkip, onCreateTeam }) {
  const [name, setName] = useState(suggestedName);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required');
      return;
    }
    setCreating(true);
    setError('');
    try {
      // Create the local Team first, then link to MatchHub (writes external_id
      // + per-company CompanyTeamNaming.confirmed_name in one go on the server).
      const team = await onCreateTeam({
        name: trimmed,
        team_type: 'club',
        sport: 'football',
      });
      if (!team || !team.id) {
        throw new Error('Team creation did not return an id');
      }
      await linkTeamToMatchHub(team.id, externalId, trimmed);
      onCreated({ ...team, name: trimmed, external_id: String(externalId) });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Create + link failed');
    } finally {
      setCreating(false);
    }
  };

  const sideLabel = side === 'home' ? 'Home team' : 'Away team';
  return (
    <div
      style={{
        border: '1px solid var(--accent-soft, rgba(0, 200, 150, 0.4))',
        background: 'rgba(0, 200, 150, 0.06)',
        borderRadius: 6,
        padding: 10,
        margin: '8px 0',
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
        {sideLabel} <em>{suggestedName}</em> isn&apos;t in Matrix yet. Adjust the local name
        (the per-company override), then create + link to MatchHub.
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          className={styles.searchInput}
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ flex: 1 }}
        />
        <Button variant="primary" onClick={handleCreate} loading={creating}>
          Create + Link
        </Button>
        <Button variant="ghost" onClick={onSkip}>
          Skip
        </Button>
      </div>
      {error && (
        <div style={{ fontSize: 12, color: 'var(--danger, #ff6b6b)', marginTop: 6 }}>{error}</div>
      )}
    </div>
  );
}

/**
 * Searchable team selector with option to create new
 */
function TeamSelector({ label, value, onChange, teams, onCreateTeam }) {
  const [showCreate, setShowCreate] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedObj, setSelectedObj] = useState(null);
  const dropdownRef = useRef(null);

  // Track the chosen team object so its name still shows for teams that aren't
  // in the (API-paginated) preloaded `teams` prop — e.g. one picked via search.
  const selectedTeam =
    selectedObj && String(selectedObj.id) === String(value)
      ? selectedObj
      : teams.find((t) => String(t.id) === String(value));

  // Server-side search: the preloaded `teams` list is capped by API pagination
  // (default 50), so client-side filtering can't see every team. Hit the backend
  // /teams/?search= via searchTeams, mirroring usePersonForm's debounced search.
  // A <2-char query falls back to a short starter list from the preloaded teams.
  const query = searchQuery.trim();
  useEffect(() => {
    // No search while closed or under 2 chars. filteredTeams ignores `results`
    // when query.length < 2, so there's nothing to clear here (keeps the effect
    // free of a synchronous setState).
    if (!isOpen || query.length < 2) return undefined;
    const t = setTimeout(async () => {
      try {
        setResults(await searchTeams(query));
      } catch (e) {
        console.error('Team search failed', e);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, isOpen]);

  const filteredTeams = query.length >= 2 ? results : teams.slice(0, 10);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (team) => {
    setSelectedObj(team || null);
    onChange(team ? String(team.id) : null);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleCreate = async () => {
    if (newTeamName.trim()) {
      const newTeam = await onCreateTeam(newTeamName.trim());
      setNewTeamName('');
      setShowCreate(false);
      if (newTeam) {
        setSelectedObj(newTeam);
        onChange(String(newTeam.id));
      }
    }
  };

  return (
    <div className={styles.formGroup}>
      <label className={styles.label}>{label}</label>
      {!showCreate ? (
        <div className={styles.teamSelectRow} ref={dropdownRef}>
          <div className={styles.teamSearchWrapper}>
            <input
              type="text"
              className={styles.input}
              placeholder="Search team..."
              value={isOpen ? searchQuery : (selectedTeam?.name || '')}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (!isOpen) setIsOpen(true);
              }}
              onFocus={() => {
                setIsOpen(true);
                setSearchQuery('');
              }}
            />
            {value && !isOpen && (
              <button
                type="button"
                className={styles.teamClearButton}
                onClick={() => handleSelect(null)}
                title="Clear selection"
              >
                x
              </button>
            )}
            {isOpen && (
              <div className={styles.teamDropdown}>
                {filteredTeams.length === 0 ? (
                  <div className={styles.teamDropdownEmpty}>No teams found</div>
                ) : (
                  filteredTeams.map((team) => (
                    <button
                      key={team.id}
                      type="button"
                      className={styles.teamDropdownItem}
                      onClick={() => handleSelect(team)}
                    >
                      {team.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowCreate(true)}
            title="Add new team"
          >
            +
          </Button>
        </div>
      ) : (
        <div className={styles.createTeamRow}>
          <input
            type="text"
            className={styles.input}
            placeholder="New team name..."
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            autoFocus
          />
          <Button variant="primary" size="sm" onClick={handleCreate}>
            Add
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Multi-select checkbox list for user assignments
 */
function UserCheckboxList({ label, users, selectedIds, onChange, autoSelectedHint }) {
  const handleToggle = (userId) => {
    const idStr = String(userId);
    if (selectedIds.includes(idStr)) {
      onChange(selectedIds.filter((id) => id !== idStr));
    } else {
      onChange([...selectedIds, idStr]);
    }
  };

  return (
    <div className={styles.formGroup}>
      <label className={styles.label}>{label}</label>
      <div className={styles.checkboxList}>
        {users.length === 0 ? (
          <span className={styles.noUsers}>No users available</span>
        ) : (
          users.map((user) => (
            <label key={user.id} className={styles.checkboxItem}>
              <input
                type="checkbox"
                checked={selectedIds.includes(String(user.id))}
                onChange={() => handleToggle(user.id)}
                className={styles.checkbox}
              />
              <span className={styles.checkboxLabel}>{user.username || user.name}</span>
            </label>
          ))
        )}
      </div>
      {autoSelectedHint && (
        <span className={styles.autoFilled}>{autoSelectedHint}</span>
      )}
    </div>
  );
}

/**
 * Step 1: Match details form
 */
function MatchDetailsForm({
  formData,
  onChange,
  teams,
  photographers,
  editors,
  onCreateTeam,
  errors,
}) {
  const updateField = useCallback((field, value) => {
    onChange((prev) => {
      const next = { ...prev, [field]: value };
      // Clear match-specific fields when switching away from match type
      if (field === 'event_type' && value !== 'match') {
        next.home_team = null;
        next.away_team = null;
        next.competition = null;
        next.kick_off_time = '';
        next.sport = 'football';
      }
      return next;
    });
  }, [onChange]);

  // Handle venue change with location auto-suggest
  const handleVenueChange = useCallback((venue) => {
    const location = lookupVenueLocation(venue);
    onChange((prev) => ({
      ...prev,
      venue,
      // Only auto-fill if location fields are empty (don't overwrite user edits)
      city: prev.city || location?.city || '',
      state: prev.state || location?.state || '',
      country: prev.country || location?.country || '',
      country_code: prev.country_code || location?.countryCode || '',
    }));
  }, [onChange]);

  // Auto-select photographer if only one available
  useEffect(() => {
    if (photographers.length === 1 && formData.photographer_ids.length === 0) {
      updateField('photographer_ids', [String(photographers[0].id)]);
    }
  }, [photographers, formData.photographer_ids, updateField]);

  return (
    <div className={styles.form}>
      {/* Event Type */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Event Type</label>
        <select
          className={styles.select}
          value={formData.event_type || 'match'}
          onChange={(e) => updateField('event_type', e.target.value)}
        >
          {Object.entries(EVENT_TYPES).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Teams (only for match type) */}
      {formData.event_type === 'match' && (
        <div className={styles.formRow}>
          <TeamSelector
            label="Home Team *"
            value={formData.home_team}
            onChange={(v) => updateField('home_team', v)}
            teams={teams}
            onCreateTeam={onCreateTeam}
          />
          <TeamSelector
            label="Away Team *"
            value={formData.away_team}
            onChange={(v) => updateField('away_team', v)}
            teams={teams}
            onCreateTeam={onCreateTeam}
          />
        </div>
      )}

      {/* Event Name */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          {formData.event_type === 'match' ? 'Match Name (optional)' : 'Event Name *'}
        </label>
        <input
          type="text"
          className={styles.input}
          placeholder={formData.event_type === 'match' ? 'e.g., Eredivisie Speelronde 21' : 'Event name...'}
          value={formData.name || ''}
          onChange={(e) => updateField('name', e.target.value)}
        />
        {errors?.name && <span className={styles.error}>{errors.name}</span>}
      </div>

      {/* Date/Time */}
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Start Time *</label>
          <input
            type="datetime-local"
            className={styles.input}
            value={formData.start_time || ''}
            onChange={(e) => updateField('start_time', e.target.value)}
          />
          {errors?.start_time && <span className={styles.error}>{errors.start_time}</span>}
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>End Time</label>
          <input
            type="datetime-local"
            className={styles.input}
            value={formData.end_time || ''}
            onChange={(e) => updateField('end_time', e.target.value)}
          />
        </div>
      </div>

      {/* Kick-off Time (match events only) */}
      {formData.event_type === 'match' && (
        <div className={styles.formGroup}>
          <label className={styles.label}>Kick-off Time</label>
          <input
            type="datetime-local"
            className={styles.input}
            value={formData.kick_off_time || ''}
            onChange={(e) => updateField('kick_off_time', e.target.value)}
            min={formData.start_time || undefined}
            max={formData.end_time || undefined}
          />
          <span className={styles.hint}>Actual kick-off. Used for before/during/after captions.</span>
        </div>
      )}

      {/* Venue and Location */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Venue</label>
        <input
          type="text"
          className={styles.input}
          placeholder="De Kuip"
          value={formData.venue || ''}
          onChange={(e) => handleVenueChange(e.target.value)}
        />
        <span className={styles.hint}>Location fields auto-fill based on venue</span>
      </div>

      {/* Location Fields */}
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.label}>City</label>
          <input
            type="text"
            className={styles.input}
            placeholder="Rotterdam"
            value={formData.city || ''}
            onChange={(e) => updateField('city', e.target.value)}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>State/Province</label>
          <input
            type="text"
            className={styles.input}
            placeholder="Zuid-Holland"
            value={formData.state || ''}
            onChange={(e) => updateField('state', e.target.value)}
          />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Country</label>
          <input
            type="text"
            className={styles.input}
            placeholder="The Netherlands"
            value={formData.country || ''}
            onChange={(e) => updateField('country', e.target.value)}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>Country Code</label>
          <input
            type="text"
            className={styles.input}
            placeholder="NLD"
            value={formData.country_code || ''}
            onChange={(e) => updateField('country_code', e.target.value)}
            maxLength={3}
          />
        </div>
      </div>

      {/* Competition (for matches) */}
      {formData.event_type === 'match' && (
        <>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Competition</label>
              <select
                className={styles.select}
                value={formData.competition || ''}
                onChange={(e) => updateField('competition', e.target.value || null)}
              >
                <option value="">Select competition...</option>
                {Object.entries(COMPETITIONS).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Sport</label>
              <select
                className={styles.select}
                value={formData.sport || 'football'}
                onChange={(e) => updateField('sport', e.target.value)}
              >
                {Object.entries(SPORTS).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Season</label>
              <select
                className={styles.select}
                value={formData.season ?? ''}
                onChange={(e) => updateField('season', e.target.value)}
              >
                {seasonOptions().map(({ value, label }) => (
                  <option key={value || 'auto'} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Round</label>
              <input
                type="text"
                className={styles.input}
                value={formData.round || ''}
                onChange={(e) => updateField('round', e.target.value)}
                placeholder="12th, Quarterfinal, Round of 16..."
                maxLength={64}
              />
            </div>
          </div>
        </>
      )}

      {/* Assignments */}
      <div className={styles.formSection}>
        <h4 className={styles.sectionTitle}>Assignments</h4>
        <div className={styles.formRow}>
          <UserCheckboxList
            label="Photographers"
            users={photographers}
            selectedIds={formData.photographer_ids}
            onChange={(ids) => updateField('photographer_ids', ids)}
            autoSelectedHint={photographers.length === 1 ? 'Auto-selected (only photographer)' : null}
          />
          <UserCheckboxList
            label="Editors"
            users={editors}
            selectedIds={formData.editor_ids}
            onChange={(ids) => updateField('editor_ids', ids)}
          />
        </div>
      </div>

      {/* Processing stages — per-match pipeline toggles. Edit (Sati) always
          runs; turning off Crop/Tag skips that stage for this match's photos. */}
      <div className={styles.formSection}>
        <h4 className={styles.sectionTitle}>Processing</h4>
        <span className={styles.hint}>
          Turn off a stage to skip it for this match&apos;s photos.
        </span>
        <div className={styles.checkboxList}>
          <label className={styles.checkboxItem} title="Edit always runs">
            <input type="checkbox" className={styles.checkbox} checked readOnly disabled />
            <span className={styles.checkboxLabel}>Edit (always on)</span>
          </label>
          <label className={styles.checkboxItem}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={formData.enable_crop !== false}
              onChange={(e) => updateField('enable_crop', e.target.checked)}
            />
            <span className={styles.checkboxLabel}>Crop</span>
          </label>
          <label className={styles.checkboxItem}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={formData.enable_tag !== false}
              onChange={(e) => updateField('enable_tag', e.target.checked)}
            />
            <span className={styles.checkboxLabel}>Tag</span>
          </label>
        </div>
      </div>
    </div>
  );
}

// Default IPTC config
const DEFAULT_IPTC_CONFIG = {
  title_template: '',
  headline_template: '',
  caption_template: '',
  city: '',
  state: '',
  country: '',
  country_code: '',
  location: '',
  event_name: '',
  keywords: [],
  credit: '',
  copyright_notice: '',
  category: 'S',
  supplemental_categories: ['SPO', 'SOC', 'FOC'],
};

/**
 * Main MatchModal component - 3-step wizard
 */
export default function MatchModal({
  isOpen,
  onClose,
  onSave,
  match = null, // null for create, object for edit
  teams = [],
  photographers = [],
  editors = [],
  onCreateTeam,
  loading = false,
  // API functions for FTP and IPTC
  fetchFtpTargets,
  fetchCompanyDefaults,
  fetchIptcPreview,
}) {
  const [mode, setMode] = useState('search'); // 'search' | 'manual'
  // Pending MatchHub fixture link (set when an editor picks a fixture); applied
  // after the match is saved, since matchhub ids are set via link_matchhub.
  const [matchHubLink, setMatchHubLink] = useState(null);
  // When a chosen fixture references teams that don't exist locally yet, list
  // them here so the wizard can render an inline create-with-override card per
  // missing side. Cleared when the editor creates or skips each.
  const [pendingTeamCreates, setPendingTeamCreates] = useState([]); // [{side, externalId, suggestedName}]
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    event_type: 'match',
    name: '',
    home_team: null,
    away_team: null,
    start_time: '',
    end_time: '',
    venue: '',
    city: '',
    state: '',
    country: '',
    country_code: '',
    competition: null,
    sport: 'football',
    kick_off_time: '',
    season: '',
    round: '',
    photographer_ids: [],
    editor_ids: [],
    enable_crop: true,
    enable_tag: true,
  });
  const [ftpTargetIds, setFtpTargetIds] = useState([]);
  const [iptcConfig, setIptcConfig] = useState(DEFAULT_IPTC_CONFIG);
  const [errors, setErrors] = useState({});

  const isEdit = !!match;

  // Initialize form data when editing - using a helper function to compute values
  // and then calling setters. This is intentional state sync from props.
  useEffect(() => {
    // Compute all new values first
    const initializeFromMatch = () => {
      if (match) {
        const photographerIds = (match.photographer_assignments || [])
          .map((a) => String(a.user_id));
        const editorIds = (match.editor_assignments || [])
          .map((a) => String(a.user_id));
        const ftpIds = (match.ftp_target_assignments || [])
          .map((a) => String(a.ftp_target_id));

        return {
          formData: {
            event_type: match.event_type || 'match',
            name: match.name || '',
            home_team: match.home_team || null,
            away_team: match.away_team || null,
            start_time: toLocalDateTimeInput(match.start_time),
            end_time: toLocalDateTimeInput(match.end_time),
            kick_off_time: toLocalDateTimeInput(match.kick_off_time),
            venue: match.venue || '',
            city: match.iptc_config?.city || '',
            state: match.iptc_config?.state || '',
            country: match.iptc_config?.country || '',
            country_code: match.iptc_config?.country_code || '',
            competition: match.competition || null,
            sport: match.sport || 'football',
            season: match.season || '',
            round: match.round || '',
            photographer_ids: photographerIds,
            editor_ids: editorIds,
            enable_crop: match.enable_crop !== false,
            enable_tag: match.enable_tag !== false,
          },
          ftpTargetIds: ftpIds,
          iptcConfig: match.iptc_config
            ? { ...DEFAULT_IPTC_CONFIG, ...match.iptc_config }
            : DEFAULT_IPTC_CONFIG,
          mode: 'manual',
          currentStep: 1,
        };
      }
      return {
        formData: null, // Keep existing
        ftpTargetIds: [],
        iptcConfig: DEFAULT_IPTC_CONFIG,
        mode: 'search',
        currentStep: 1,
      };
    };

    const values = initializeFromMatch();

    // Apply state updates (intentional sync from props on modal open)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (values.formData) setFormData(values.formData);
    setFtpTargetIds(values.ftpTargetIds);
    setIptcConfig(values.iptcConfig);
    setMode(values.mode);
    setCurrentStep(values.currentStep);
  }, [match, isOpen]);

  // Validate step 1
  const validateStep1 = useCallback(() => {
    const errs = {};

    if (formData.event_type === 'match') {
      if (!formData.home_team) errs.home_team = 'Required';
      if (!formData.away_team) errs.away_team = 'Required';
    } else {
      if (!formData.name?.trim()) errs.name = 'Required';
    }

    if (!formData.start_time) errs.start_time = 'Required';

    if (formData.kick_off_time) {
      if (formData.start_time && formData.kick_off_time < formData.start_time) {
        errs.kick_off_time = 'Must be after start time';
      }
      if (formData.end_time && formData.kick_off_time > formData.end_time) {
        errs.kick_off_time = 'Must be before end time';
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [formData]);

  // Navigate to next step
  const handleNext = () => {
    if (currentStep === 1) {
      if (!validateStep1()) return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, 3));
  };

  // Navigate to previous step
  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Handle save
  const handleSave = async () => {
    if (!validateStep1()) {
      setCurrentStep(1);
      return;
    }

    const data = {
      ...formData,
      start_time: new Date(formData.start_time).toISOString(),
      end_time: formData.end_time ? new Date(formData.end_time).toISOString() : null,
      kick_off_time: formData.kick_off_time
        ? new Date(formData.kick_off_time).toISOString()
        : null,
      // Coerce null competition to empty string (backend CharField doesn't accept null)
      competition: formData.competition || '',
      // Omit null team fields for non-match events
      home_team: formData.home_team || null,
      away_team: formData.away_team || null,
      // Convert string IDs to appropriate format for the API
      photographer_ids: formData.photographer_ids,
      editor_ids: formData.editor_ids,
      ftp_target_ids: ftpTargetIds,
      iptc_config: iptcConfig,
    };

    // matchHubLink (if any) is applied by the parent after the match is saved,
    // via the link_matchhub action (matchhub ids are read-only on the serializer).
    await onSave(data, matchHubLink);
  };

  // Handle MatchHub fixture selection. Resolves local Teams via external_id;
  // when one side has no local row, queues a PendingTeamCard so the editor
  // can create-with-override before continuing. Always derives the shooting
  // window (start = kickoff − 5h, end = kickoff + 5h) per the fixed rule.
  const handleMatchHubSelect = async (fixture) => {
    const home = fixture.home || {};
    const away = fixture.away || {};
    const teamsLabel = home.name && away.name ? `${home.name} vs ${away.name}` : '';
    const kickoff = fixture.kickoff ? new Date(fixture.kickoff) : null;
    const startIso = kickoff ? new Date(kickoff.getTime() - 5 * 60 * 60 * 1000) : null;
    const endIso = kickoff ? new Date(kickoff.getTime() + 5 * 60 * 60 * 1000) : null;
    const seasonId = fixture.season?.id;

    // Resolve local home/away by external_id. Missing ones queue an inline
    // create card; the editor must address each before saving.
    const localHome = home.id ? await fetchTeamByExternalId(home.id).catch(() => null) : null;
    const localAway = away.id ? await fetchTeamByExternalId(away.id).catch(() => null) : null;
    const pending = [];
    if (home.id && !localHome) {
      pending.push({ side: 'home', externalId: String(home.id), suggestedName: home.name || '' });
    }
    if (away.id && !localAway) {
      pending.push({ side: 'away', externalId: String(away.id), suggestedName: away.name || '' });
    }
    setPendingTeamCreates(pending);

    setFormData((prev) => ({
      ...prev,
      name: teamsLabel,
      home_team: localHome ? localHome.id : prev.home_team,
      away_team: localAway ? localAway.id : prev.away_team,
      kick_off_time: kickoff ? toLocalDateTimeInput(kickoff) : prev.kick_off_time,
      start_time: startIso ? toLocalDateTimeInput(startIso) : prev.start_time,
      end_time: endIso ? toLocalDateTimeInput(endIso) : prev.end_time,
      competition: fixture.season?.competition?.name || prev.competition,
      season: fixture.season?.year ? String(fixture.season.year) : prev.season,
      round: fixture.round || prev.round,
      sport: prev.sport || 'football',
    }));
    if (seasonId) {
      setMatchHubLink({ fixtureId: String(fixture.id), seasonId: String(seasonId) });
    }
    setMode('manual');
  };

  // PendingTeamCard onCreated callback — set the new Team on the corresponding
  // side and clear the card.
  const onPendingTeamCreated = (side, team) => {
    setFormData((prev) => ({
      ...prev,
      [side === 'home' ? 'home_team' : 'away_team']: team.id,
    }));
    setPendingTeamCreates((prev) => prev.filter((p) => p.side !== side));
  };

  const onPendingTeamSkipped = (side) => {
    setPendingTeamCreates((prev) => prev.filter((p) => p.side !== side));
  };

  // Render current step content
  const renderStepContent = () => {
    if (mode === 'search' && !isEdit && currentStep === 1) {
      return (
        <MatchHubSearch
          onSelect={handleMatchHubSelect}
          onManualEntry={() => setMode('manual')}
        />
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <>
            {pendingTeamCreates.map((p) => (
              <PendingTeamCard
                key={p.side}
                side={p.side}
                externalId={p.externalId}
                suggestedName={p.suggestedName}
                onCreateTeam={onCreateTeam}
                onCreated={(team) => onPendingTeamCreated(p.side, team)}
                onSkip={() => onPendingTeamSkipped(p.side)}
              />
            ))}
            <MatchDetailsForm
              formData={formData}
              onChange={setFormData}
              teams={teams}
              photographers={photographers}
              editors={editors}
              onCreateTeam={onCreateTeam}
              errors={errors}
            />
          </>
        );
      case 2:
        return (
          <FtpSelectionStep
            selectedIds={ftpTargetIds}
            onChange={setFtpTargetIds}
            fetchFtpTargets={fetchFtpTargets}
          />
        );
      case 3:
        return (
          <IptcConfigStep
            config={iptcConfig}
            onChange={setIptcConfig}
            matchData={formData}
            teams={teams}
            fetchCompanyDefaults={fetchCompanyDefaults}
            fetchPreview={fetchIptcPreview}
          />
        );
      default:
        return null;
    }
  };

  // Step labels
  const stepLabels = ['Match Details', 'FTP Destinations', 'IPTC Config'];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" className={styles.drawerB}>
      <Modal.Header onClose={onClose}>
        {isEdit ? 'Edit Match' : 'Create Match'}
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        {/* Stepper - only show in manual mode */}
        {mode === 'manual' && (
          <Stepper currentStep={currentStep}>
            <Stepper.Step number={1} label={stepLabels[0]} />
            <Stepper.Step number={2} label={stepLabels[1]} />
            <Stepper.Step number={3} label={stepLabels[2]} />
          </Stepper>
        )}

        {renderStepContent()}
      </Modal.Body>

      <Modal.Footer>
        {/* Back button */}
        {mode === 'manual' && currentStep > 1 && (
          <Button variant="ghost" onClick={handleBack}>
            Back
          </Button>
        )}

        {/* Back to search button */}
        {mode === 'manual' && currentStep === 1 && !isEdit && (
          <Button variant="ghost" onClick={() => setMode('search')}>
            Back to Search
          </Button>
        )}

        <div style={{ flex: 1 }} />

        {/* Cancel button */}
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>

        {/* Next/Create button */}
        {mode === 'manual' && (
          <>
            {currentStep < 3 ? (
              <Button variant="primary" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button variant="primary" onClick={handleSave} loading={loading}>
                {isEdit ? 'Save Changes' : 'Create Match'}
              </Button>
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
